// eslint-disable-next-line max-classes-per-file
'use client';

import useFetcher, { AuthenticatedFetcher } from 'app/_utils/data/useFetcher';
import { baseApiUrl, baseTenantsApiUrlV3 } from 'src/utils/api';

import React, { useContext, useRef } from 'react';
import { MemberAccessCheckRequestItem, MemberAccessCheckResponse, MemberAccessCheckType, WorkspaceAccess } from 'shared/src/generated/openapi/v3/types';
import { Product } from 'shared/src/types';

type WithPermissions<T extends string = string> = {
  permissions: readonly T[];
};
type WithPermission = {
  permission: string;
  // This serialization field is an internal concept to help track all the parts that make a check unique
  // (org, workspace, product, actual permission etc). This is helpful for caching, particularly on the
  // level of individual checks. The caller should not need to know about this and it should not leak out
  serialization: CheckSerialization;
};
type WithResults<T extends string = string> = {
  permissions: {
    [permission in T]: boolean;
  };
};

export type SystemChecksRequest<T extends string = string> = WithPermissions<T> & {
  type: 'system';
};
export type SystemChecksResult<T extends string = string> = WithResults<T>;
type SystemCheck = WithPermission & {
  type: 'system';
};

interface OrganizationContext {
  organization: string;
}
export type OrganizationChecksRequest<T extends string = string> = WithPermissions<T> & OrganizationContext & {
  type: 'organization';
};
export type OrganizationChecksResult<T extends string = string> = OrganizationContext & WithResults<T>;
type OrganizationCheck = OrganizationContext & WithPermission & {
  type: 'organization';
};

interface WorkspaceContext {
  organization: string;
  workspace: string;
}
export type WorkspaceChecksRequest<T extends string = string> = WithPermissions<T> & WorkspaceContext & {
  type: 'workspace';
};
export type WorkspaceChecksResult<T extends string = string> = WorkspaceContext & WithResults<T>;
type WorkspaceCheck = WorkspaceContext & WithPermission & {
  type: 'workspace';
};

interface ProductContext {
  organization: string;
  workspace: string;
  product: Product;
}
export type ProductChecksRequest<T extends string = string> = WithPermissions<T> & ProductContext & {
  type: 'product';
};
export type ProductChecksResult<T extends string = string> = ProductContext & WithResults<T>;
type ProductCheck = ProductContext & WithPermission & {
  type: 'product';
};

export type ChecksRequest<T extends string = string> =
  SystemChecksRequest<T> | OrganizationChecksRequest<T> | WorkspaceChecksRequest<T> | ProductChecksRequest<T>;
export type ChecksResult<T extends string = string> =
  SystemChecksResult<T> | OrganizationChecksResult<T> | WorkspaceChecksResult<T> | ProductChecksResult<T>;
type IndividualCheck = SystemCheck | OrganizationCheck | WorkspaceCheck | ProductCheck;

export interface CheckPermissionsOptions {
  consistency?: 'high' | 'standard';
}
export interface IAccessChecker {
  checkPermissions<T extends string = string>(
    checks: SystemChecksRequest<T>[], options?: CheckPermissionsOptions
  ): Promise<SystemChecksResult<T>[]>;
  checkPermissions<T extends string = string>(
    checks: OrganizationChecksRequest<T>[], options?: CheckPermissionsOptions
  ): Promise<OrganizationChecksResult<T>[]>;
  checkPermissions<T extends string = string>(
    checks: WorkspaceChecksRequest<T>[], options?: CheckPermissionsOptions
  ): Promise<WorkspaceChecksResult<T>[]>;
  checkPermissions<T extends string = string>(
    checks: ProductChecksRequest<T>[], options?: CheckPermissionsOptions
  ): Promise<ProductChecksResult<T>[]>;
  checkPermissions<T extends string = string>(
    checks: ChecksRequest<T>[], options?: CheckPermissionsOptions
  ): Promise<ChecksResult<T>[]>;
}

type CheckResultMap = Record<string, boolean>; // permission -> access result
type WorkspaceName = string;

export interface IAccessFetcher {
  fetchSystemAccess(
    systemChecks: SystemCheck[], options: CheckPermissionsOptions
  ): Promise<CheckResultMap>;
  fetchOrgAccess(
    orgChecks: OrganizationCheck[], options: CheckPermissionsOptions
  ): Promise<CheckResultMap>;
  fetchWorkspaceAccess(
    workspaceChecks: WorkspaceCheck[], options: CheckPermissionsOptions
  ): Promise<Record<WorkspaceName, WorkspaceAccess>>; // workspace -> perm -> result
  fetchProductAccess(
    productChecks: ProductCheck[], options: CheckPermissionsOptions
  ): Promise<Record<WorkspaceName, Record<string, CheckResultMap>>>; // workspace -> product -> perm -> result
}

export const GlobalAccessProvider = React.createContext<IAccessChecker | null>(null);

/**
 * Hook to access the global access context
 * @returns The global access context
 * @throws Error if used outside of GlobalAccessProvider
 */
export function useGlobalAccess(): IAccessChecker {
  const context = useContext(GlobalAccessProvider);

  if (context === null) {
    throw new Error('useGlobalAccess must be used within a GlobalAccessProvider');
  }

  return context;
}

interface Props {
  children: React.ReactNode;
}

export const GlobalAccessCache: React.FC<Props> = ({ children }) => {
  const { fetcher } = useFetcher();
  const checker = useRef(new AccessChecker(new AccessFetcher(fetcher)));

  return (
    <GlobalAccessProvider.Provider value={checker.current}>
      {children}
    </GlobalAccessProvider.Provider>
  );
};

interface CheckResponseRecord {
  allowed: boolean;
  validUntil: number;
}

const defaultDuration = 120_000;
type CheckSerialization = `system:${string}` | `organization:${string}`;
export class AccessChecker implements IAccessChecker {
  private permissionsCache: Record<string, CheckResponseRecord | undefined> = {};

  constructor(
    private fetcher: IAccessFetcher,
    private cacheDuration: number = defaultDuration,
  ) { }

  checkPermissions<T extends string = string>(
    checks: SystemChecksRequest<T>[], options?: CheckPermissionsOptions
  ): Promise<SystemChecksResult<T>[]>;
  checkPermissions<T extends string = string>(
    checks: OrganizationChecksRequest<T>[], options?: CheckPermissionsOptions
  ): Promise<OrganizationChecksResult<T>[]>;
  checkPermissions<T extends string = string>(
    checks: WorkspaceChecksRequest<T>[], options?: CheckPermissionsOptions
  ): Promise<WorkspaceChecksResult<T>[]>;
  checkPermissions<T extends string = string>(
    checks: ProductChecksRequest<T>[], options?: CheckPermissionsOptions
  ): Promise<ProductChecksResult<T>[]>;
  checkPermissions<T extends string = string>(
    checks: ChecksRequest<T>[], options?: CheckPermissionsOptions
  ): Promise<ChecksResult<T>[]>;
  async checkPermissions(
    checks: ChecksRequest[], options: CheckPermissionsOptions = { consistency: 'standard' },
  ): Promise<ChecksResult[]> {
    const batchedChecks: IndividualCheck[][] = checks.map((check) => check.permissions.map((permission) => ({
      ...check,
      permission: permission,
      serialization: this.serializeCheck(check, permission),
    })));
    const unresolved: IndividualCheck[] = [];
    const results: Record<CheckSerialization, boolean> = {};

    const now = Date.now();

    for (const check of batchedChecks.flat()) {
      const cachedVal = this.permissionsCache[check.serialization];

      if (options.consistency !== 'high' && cachedVal && cachedVal.validUntil > now) {
        results[check.serialization] = cachedVal.allowed;
      } else {
        unresolved.push(check);
      }
    }

    const fetchResults = await this.fetchAccess(unresolved, options);
    Object.entries(fetchResults).forEach(([key, access]) => {
      // This default value is intentionally not cached. If something went wrong and no result came back, we treat this
      // as a failed permission check but will try again in the future
      results[key as CheckSerialization] = access ?? false;
    });

    return batchedChecks.map((batch, i) => ({
      ...checks[i],
      permissions: Object.fromEntries(batch.map(
        (check) => [check.permission, results[check.serialization]],
      )),
    }));
  }

  private async fetchAccess(
    checks: IndividualCheck[], options: CheckPermissionsOptions,
  ): Promise<Record<CheckSerialization, boolean>> {
    if (checks.length === 0) {
      return {};
    }

    const hasOrg = ((check) => check.type !== 'system') as (check: IndividualCheck) => check is OrganizationCheck | WorkspaceCheck | ProductCheck;
    const checkedOrgs = new Set(checks
      .filter(hasOrg)
      .map((check: OrganizationCheck | WorkspaceCheck | ProductCheck) => check.organization),
    );

    // This simplifies the implementation a fair bit and I don't think our UI desperately needs multi-org permissions
    // checks immediately. We could take out this requirement in the future if needed
    if (checkedOrgs.size > 1) {
      throw new Error('Permissions checks support a single organization per batch');
    }

    const checksByType = Object.groupBy(checks, (check) => check.type);

    let results: Record<string, boolean> = {};

    if (checksByType.system?.length) {
      const systemResults = await this.fetcher.fetchSystemAccess(checksByType.system as SystemCheck[], options);
      const serialized = Object.fromEntries(checksByType.system.map((check) => [
        check.serialization,
        systemResults[check.permission],
      ]));
      results = { ...results, ...serialized };
    }

    if (checksByType.organization?.length) {
      const orgResults = await this.fetcher.fetchOrgAccess(
        checksByType.organization as OrganizationCheck[], options,
      );
      const serialized = Object.fromEntries(checksByType.organization.map((check) => [
        check.serialization,
        orgResults?.[check.permission],
      ]));
      results = { ...results, ...serialized };
    }

    if (checksByType.workspace?.length) {
      const workspaceResults = await this.fetcher.fetchWorkspaceAccess(
        checksByType.workspace as WorkspaceCheck[], options,
      );
      const serialized = Object.fromEntries((checksByType.workspace as WorkspaceCheck[]).map((check) => [
        check.serialization,
        (workspaceResults[check.workspace]?.[check.permission] as unknown as boolean | undefined) ?? false,
      ]));
      results = { ...results, ...serialized };
    }

    if (checksByType.product?.length) {
      const productResults = await this.fetcher.fetchProductAccess(
        checksByType.product as ProductCheck[], options,
      );
      const serialized = Object.fromEntries((checksByType.product as ProductCheck[]).map((check) => [
        check.serialization,
        productResults[check.workspace][check.product]?.[check.permission],
      ]));
      results = { ...results, ...serialized };
    }

    this.cachePermissions(checks, results);

    return results;
  }

  private cachePermissions(checks: IndividualCheck[], results: Record<CheckSerialization, boolean>) {
    for (const check of checks) {
      const result = results[check.serialization];

      if (result !== undefined) {
        this.permissionsCache[check.serialization] = { allowed: result, validUntil: Date.now() + this.cacheDuration };
      }
    }
  }

  private serializeCheck(check: ChecksRequest, permission: string): CheckSerialization {
    switch (check.type) {
      case 'system':
        return `system:${permission}`;
      case 'organization':
        return `organization:${check.organization}:${permission}`;
      case 'workspace':
        return `organization:${check.organization}:${check.workspace}:${permission}`;
      case 'product':
        return `organization:${check.organization}:${check.workspace}:${check.product}:${permission}`;
      default:
        throw new Error('unknown type');
    }
  }
}

export class AccessFetcher implements IAccessFetcher {
  constructor(private fetcher: AuthenticatedFetcher) { }

  async fetchSystemAccess(systemChecks: SystemCheck[], options: CheckPermissionsOptions) {
    const resp = await this.fetcher(`${baseApiUrl}/v2/system/access${this.buildQueryParams(options)}`, {
      method: 'POST',
      body: JSON.stringify({
        checks: systemChecks.map((check) => check.permission),
      }),
    }) as { checks: Record<string, boolean> };

    return resp.checks ?? {};
  }

  async fetchOrgAccess(orgChecks: OrganizationCheck[], options: CheckPermissionsOptions) {
    const request: MemberAccessCheckRequestItem[] = [{
      accessType: MemberAccessCheckType.Organization,
      checks: orgChecks.map((check) => check.permission),
    }];
    const resp = await this.fetcher(`${baseTenantsApiUrlV3}/${orgChecks[0].organization}/members/self/access${this.buildQueryParams(options)}`, {
      method: 'POST',
      body: JSON.stringify(request),
    }) as MemberAccessCheckResponse;

    return resp.organization ?? {};
  }

  async fetchWorkspaceAccess(workspaceChecks: WorkspaceCheck[], options: CheckPermissionsOptions) {
    const workspaceGroups = Object.groupBy(workspaceChecks, (check) => check.workspace);
    const request: MemberAccessCheckRequestItem[] = Object.entries(workspaceGroups).map(([workspace, checks]) => ({
      accessType: MemberAccessCheckType.Workspace,
      workspaceId: workspace,
      checks: (checks ?? []).map((check) => check.permission),
    }));
    const resp = await this.fetcher(`${baseTenantsApiUrlV3}/${workspaceChecks[0].organization}/members/self/access${this.buildQueryParams(options)}`, {
      method: 'POST',
      body: JSON.stringify(request),
    }) as MemberAccessCheckResponse;

    return resp.workspaces ?? {};
  }

  async fetchProductAccess(productChecks: ProductCheck[], options: CheckPermissionsOptions) {
    const byWorkspace = Object.groupBy(productChecks, (check) => check.workspace);
    const request: MemberAccessCheckRequestItem[] = Object.entries(byWorkspace).map(([workspace, products]) => {
      const byProduct = Object.groupBy(products ?? [], (product) => product.product);

      return {
        accessType: MemberAccessCheckType.Product,
        workspaceId: workspace,
        products: Object.entries(byProduct)
          .map(([product, checks]) => ({
            productName: product,
            checks: (checks ?? []).map((check) => check.permission),
          })),
      };
    });
    const resp = await this.fetcher(`${baseTenantsApiUrlV3}/${productChecks[0].organization}/members/self/access${this.buildQueryParams(options)}`, {
      method: 'POST',
      body: JSON.stringify(request),
    }) as MemberAccessCheckResponse;

    const resultMap = Object.fromEntries(
      Object.entries(resp.workspaces ?? {})
        .map(([workspace, workspaceResult]) => [workspace, workspaceResult.products ?? {}]),
    );

    return resultMap;
  }

  private buildQueryParams(options: CheckPermissionsOptions) {
    const params = new URLSearchParams();

    if (options.consistency === 'high') {
      params.append('consistency', 'high');
    }

    return `?${params.toString()}`;
  }
}
